import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt } from './_finHelpers';

// 2026 estate tax exemption (approx)
const EXEMPTION_2026 = 15000000;

// Estate Tax Calculator — mirrors image
export default function EstateTaxCalc() {
  const [realEstate, setRealEstate] = useState(0);
  const [investments, setInvestments] = useState(0);
  const [savings, setSavings] = useState(0);
  const [vehicles, setVehicles] = useState(0);
  const [retirement, setRetirement] = useState(0);
  const [lifeIns, setLifeIns] = useState(0);
  const [otherAssets, setOtherAssets] = useState(0);
  const [debts, setDebts] = useState(0);
  const [funeral, setFuneral] = useState(0);
  const [charity, setCharity] = useState(0);
  const [stateTax, setStateTax] = useState(0);
  const [gifted, setGifted] = useState(0);

  const c = useMemo(() => {
    const totalAssets = realEstate + investments + savings + vehicles + retirement + lifeIns + otherAssets;
    const totalDeductions = debts + funeral + charity + stateTax;
    const grossEstate = totalAssets - totalDeductions;
    const effectiveExemption = Math.max(0, EXEMPTION_2026 - gifted);
    const taxable = Math.max(0, grossEstate - effectiveExemption);
    // Federal estate tax rate = 40% for amount over exemption
    const tax = taxable * 0.40;
    return { totalAssets, totalDeductions, grossEstate, effectiveExemption, taxable, tax };
  }, [realEstate, investments, savings, vehicles, retirement, lifeIns, otherAssets, debts, funeral, charity, stateTax, gifted]);

  const $ = (v, on) => <div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={v} onChange={e=>on(+e.target.value||0)} className="flex-1"/></div>;

  return (
    <CalcShell look="cnet" title="Estate Tax Calculator" subtitle="Estimate federal estate tax due on inheritance based on 2026 exemption and 40% rate."
      about={<><p>The <b>federal estate tax</b> is levied on the transfer of an individual's assets at death. For 2026, the exemption is approximately $15M/person. Amounts above the exemption are taxed at up to 40%.</p></>}
    >
      <div className={`result-panel mb-6 ${c.tax > 0 ? 'bg-red-50' : 'bg-green-50'}`} style={{background: c.tax > 0 ? '#fee2e2' : '#dcfce7'}}>
        <div className="result-title" style={{color: c.tax > 0 ? '#991b1b' : '#166534'}}>Result</div>
        <div className="text-sm text-slate-700 mt-1">Net taxable estate is <b data-testid="estate-taxable">{fmt(c.taxable)}</b>.</div>
        <div className="text-sm text-slate-700 mt-1">{c.tax === 0 ? 'Because the taxable estate value is within the exemption, the federal estate tax due is $0 for 2026.' : `Federal estate tax due (40%): ${fmt(c.tax)}.`}</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Assets</h3>
          <div className="field"><label>Residence & Other Real Estate</label>{$(realEstate, setRealEstate)}</div>
          <div className="field"><label>Stocks, Bonds, and Other Investments</label>{$(investments, setInvestments)}</div>
          <div className="field"><label>Savings, CDs, and Checking Account Balance</label>{$(savings, setSavings)}</div>
          <div className="field"><label>Vehicles, Boats, and Other Properties</label>{$(vehicles, setVehicles)}</div>
          <div className="field"><label>Retirement Plans</label>{$(retirement, setRetirement)}</div>
          <div className="field"><label>Life Insurance Benefit</label>{$(lifeIns, setLifeIns)}</div>
          <div className="field"><label>Other Assets</label>{$(otherAssets, setOtherAssets)}</div>
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 mb-3">Liability, Costs, and Deductibles</h3>
          <div className="field"><label>Debts (mortgages, loan, credit cards, etc.)</label>{$(debts, setDebts)}</div>
          <div className="field"><label>Funeral, Administration, and Claims Expenses</label>{$(funeral, setFuneral)}</div>
          <div className="field"><label>Charitable Contributions</label>{$(charity, setCharity)}</div>
          <div className="field"><label>State Inheritance or Estate Taxes</label>{$(stateTax, setStateTax)}</div>
          <h3 className="font-semibold text-slate-900 mb-3 mt-4">Lifetime Gifted Amount</h3>
          <div className="field"><label>Total amount you've gifted tax free in your lifetime</label>{$(gifted, setGifted)}</div>
          <table className="data-table mt-4"><tbody>
            <tr><td>Total Assets</td><td className="text-right">{fmt(c.totalAssets)}</td></tr>
            <tr><td>Total Deductions</td><td className="text-right">-{fmt(c.totalDeductions)}</td></tr>
            <tr><td>Gross Estate</td><td className="text-right font-semibold">{fmt(c.grossEstate)}</td></tr>
            <tr><td>Effective Exemption (2026)</td><td className="text-right">-{fmt(c.effectiveExemption)}</td></tr>
            <tr className="border-t-2 font-semibold"><td>Net Taxable Estate</td><td className="text-right">{fmt(c.taxable)}</td></tr>
            <tr><td>Federal Estate Tax (40%)</td><td className="text-right text-red-600">{fmt(c.tax)}</td></tr>
          </tbody></table>
        </div>
      </div>
    </CalcShell>
  );
}
